/**
 * Manager for date-related operations in the chart modal
 * Handles date range calculations and label generation
 */
export class ChartDateManager {
  /**
   * Ensure dates are Date objects, not strings
   */
  ensureDateObject(date: Date | string | null): Date | null {
    if (!date) return null;
    if (date instanceof Date) return date;
    return new Date(date);
  }

  /**
   * Generate labels for the date range in YYYY-MM format
   */
  generateMonthLabels(startDate: Date | null, endDate: Date | null): string[] {
    if (!startDate || !endDate) return [];

    const start = this.ensureDateObject(startDate);
    const end = this.ensureDateObject(endDate);
    if (!start || !end) return [];

    const startYear = start.getFullYear();
    const startMonth = start.getMonth();
    const endYear = end.getFullYear();
    const endMonth = end.getMonth();

    const startDateNorm = new Date(startYear, startMonth, 1);
    let endDateNorm = new Date(endYear, endMonth, 1);

    // Clamp to current month (no future data)
    const now = new Date();
    const currentMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    if (endDateNorm > currentMonthStart) {
      endDateNorm = currentMonthStart;
    }

    if (startDateNorm > endDateNorm) return [];

    const labels: string[] = [];
    const cursor = new Date(startDateNorm.getFullYear(), startDateNorm.getMonth(), 1);

    while (cursor <= endDateNorm) {
      const y = cursor.getFullYear();
      const m = (cursor.getMonth() + 1).toString().padStart(2, '0');
      labels.push(`${y}-${m}`);
      cursor.setMonth(cursor.getMonth() + 1);
    }

    return labels;
  }


  /**
   * Get date range parameters for API calls
   */
  getDateRangeParams(startDate: Date | null, endDate: Date | null): {
    startYear: number;
    startMonth: number;
    endYear: number;
    endMonth: number;
    monthsInRange: number;
  } {
    const start = this.ensureDateObject(startDate) || new Date();
    const end = this.ensureDateObject(endDate) || new Date();

    const startYear = start.getFullYear();
    const startMonth = start.getMonth() + 1;
    const endYear = end.getFullYear();
    const endMonth = end.getMonth() + 1;

    // Clamp to current month
    const now = new Date();
    const currentMonth = now.getMonth() + 1;
    const currentYear = now.getFullYear();

    let actualEndMonth = endMonth;
    let actualEndYear = endYear;
    if (endYear > currentYear || (endYear === currentYear && endMonth > currentMonth)) {
      actualEndYear = currentYear;
      actualEndMonth = currentMonth;
    }

    const monthsInRange = (actualEndYear - startYear) * 12 + (actualEndMonth - startMonth) + 1;

    return {
      startYear,
      startMonth,
      endYear: actualEndYear,
      endMonth: actualEndMonth,
      monthsInRange
    };
  }
}
